import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { ShieldCheck, ArrowRight, ArrowUp, ArrowDown, Lock, Heart } from "lucide-react";
import { API } from "../lib/api";

const publicApi = axios.create({ baseURL: API, withCredentials: false });

export default function DonorPortal() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    publicApi
      .get(`/public/donor/${token}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.detail || "This portal link is invalid or revoked."));
  }, [token]);

  // Inject donor portal-friendly OG / page-title tags
  useEffect(() => {
    if (!data) return;
    const orgName = data.branding?.org_name || "Array Engine";
    const donorName = data.portal?.donor_name || "Donor";
    const prev = document.title;
    document.title = `${donorName} · ${orgName} — Impact Portal`;
    return () => { document.title = prev; };
  }, [data]);

  if (error) {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50 p-6">
        <div className="max-w-md text-center bg-white border border-slate-200 p-10 rounded-sm" data-testid="donor-portal-error">
          <Lock className="w-10 h-10 mx-auto text-slate-400" />
          <h1 className="font-heading text-2xl mt-4">Portal unavailable</h1>
          <p className="text-slate-600 mt-2 text-sm">{error}</p>
        </div>
      </div>
    );
  }
  if (!data) return <div className="min-h-screen grid place-items-center text-slate-500">Loading donor portal…</div>;

  const branding = data.branding || {};
  const orgLogo = branding.org_logo_url;
  const orgName = branding.org_name || "Array Engine";

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {orgLogo ? (
              <img src={orgLogo} alt={orgName} className="h-8 w-auto max-w-[180px] object-contain" data-testid="donor-org-logo" />
            ) : (
              <img src="/array-logo.png" alt="Array Engine" className="h-7 w-auto" data-testid="donor-array-logo" />
            )}
            {orgLogo && (
              <span className="hidden sm:inline-block text-sm text-slate-700 font-medium">{orgName}</span>
            )}
          </div>
          <span className="hidden sm:flex items-center gap-1 text-xs text-slate-500">
            <ShieldCheck className="w-3 h-3" /> Donor portal · token-protected
          </span>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-12" data-testid="donor-portal">
        <section className="mb-10">
          <p className="label-overline flex items-center gap-1.5">
            <Heart className="w-3 h-3 text-emerald-600" /> Welcome
          </p>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-slate-900 mt-2 tracking-tight" data-testid="donor-welcome-title">
            Hi {data.portal.donor_name}.
          </h1>
          <p className="text-slate-600 mt-3 max-w-2xl">
            Here's the impact you've helped make possible across {data.programs.length} program{data.programs.length === 1 ? "" : "s"}.
            Every number traces back to source data — click any value to verify.
          </p>
          {data.portal.welcome_note && (
            <div className="mt-6 p-4 bg-emerald-50 border-l-4 border-emerald-500 rounded-sm" data-testid="donor-welcome-note">
              <p className="text-sm text-slate-800 leading-relaxed whitespace-pre-wrap">{data.portal.welcome_note}</p>
            </div>
          )}
        </section>

        <section data-testid="donor-program-grid">
          <p className="label-overline mb-4">Programs · {data.programs.length}</p>
          {data.programs.length === 0 ? (
            <div className="text-center py-10 text-slate-400 text-sm border border-dashed border-slate-200 rounded-md">
              No programs in this portal yet.
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-4">
              {data.programs.map((p) => (
                <Link
                  key={p.id}
                  to={`/donor/${token}/program/${p.id}`}
                  className="block bg-white border border-slate-200 rounded-md p-5 hover:border-emerald-400 hover:shadow-sm transition group"
                  data-testid={`donor-program-card-${p.id}`}
                >
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <h2 className="font-heading text-lg font-semibold text-slate-900 group-hover:text-emerald-700">{p.name}</h2>
                    <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-emerald-600 transition flex-shrink-0 mt-1" />
                  </div>
                  {p.description && (
                    <p className="text-xs text-slate-500 mb-4 line-clamp-2">{p.description}</p>
                  )}
                  {p.hero ? (
                    <div className="bg-slate-900 text-white p-4 rounded-sm">
                      <p className="text-[10px] uppercase tracking-wider text-slate-300">{p.hero.label}</p>
                      <p className="font-mono text-3xl font-semibold mt-1 text-emerald-300">
                        {Number(p.hero.value || 0).toLocaleString()}
                        <span className="text-sm font-body font-normal text-slate-300 ml-1.5">{p.hero.unit}</span>
                      </p>
                      <div className="flex items-center justify-between mt-2 text-[11px] text-slate-400">
                        <span>{p.hero.period_range}</span>
                        {p.hero.delta_pct !== null && p.hero.delta_pct !== undefined && (
                          <span className={`inline-flex items-center gap-0.5 ${p.hero.delta_pct >= 0 ? "text-emerald-300" : "text-rose-300"}`}>
                            {p.hero.delta_pct >= 0 ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
                            {Math.abs(p.hero.delta_pct).toFixed(Math.abs(p.hero.delta_pct) >= 10 ? 0 : 1)}%
                          </span>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="bg-slate-50 border border-slate-200 p-4 rounded-sm text-xs text-slate-400 text-center">
                      No data published yet
                    </div>
                  )}
                  <p className="text-[11px] text-slate-400 mt-3 group-hover:text-emerald-700">
                    View full report →
                  </p>
                </Link>
              ))}
            </div>
          )}
        </section>

        <footer className="mt-16 pt-6 border-t border-slate-200 text-xs text-slate-500 flex items-center justify-between flex-wrap gap-2">
          <span>Numbers shown are aggregated from validated source data. Click any value inside a report to inspect the audit trail.</span>
          <span className="label-overline">
            {orgLogo ? `${orgName} · ` : ""}Powered by Array Engine
          </span>
        </footer>
      </main>
    </div>
  );
}
